import React, { Component } from 'react'
import L from 'leaflet'
import ProgramLocationProps from '@interfaces/ProgramLocationProps'

const { I18n } = window

const POINT_ZOOM = 15

// Blank or non-numeric input (`abc`, the French decimal comma `45,5017`, a lone `-` mid-typing)
// gives null, so callers leave the pin alone; Leaflet throws on NaN.
const toLatLng = (lat: string | number, lng: string | number): L.LatLng | null => {
  if (lat == null || lng == null || String(lat).trim() === '' || String(lng).trim() === '') {
    return null
  }

  const latNumber = Number(lat)
  const lngNumber = Number(lng)

  if (!isFinite(latNumber) || !isFinite(lngNumber)) return null

  return L.latLng(latNumber, lngNumber)
}

class LocationInfoControl extends L.Control {
  locationModel: ProgramLocationProps

  constructor(location: ProgramLocationProps, controlOptions: L.ControlOptions) {
    super(controlOptions)
    this.locationModel = location
  }

  onAdd = (): HTMLElement => {
    const div = L.DomUtil.create('div')
    div.className = 'location-tooltip custom-control'
    div.style.width = '200px'
    div.innerHTML = `
      <address>
        ${this.locationModel.formatted_address}
      </address>
      <a href="https://www.google.com/maps/search/?api=1&query=${this.locationModel.latitude},${
      this.locationModel.longitude
    }" target="_blank">
        ${I18n.t('views.ke_map.view_on_google')}
      </a>
    `

    return div
  }
}

class ResetMarkerButton extends L.Control {
  callback: () => void
  defaultVisibility: boolean

  constructor(callback: () => void, controlOptions: L.ControlOptions, defaultVisibility: boolean) {
    super(controlOptions)
    this.callback = callback
    this.defaultVisibility = defaultVisibility
  }

  onAdd = (): HTMLButtonElement => {
    const button = L.DomUtil.create('button') as HTMLButtonElement
    button.className = 'reset-marker-button leaflet-touch leaflet-bar custom-control'
    button.style.display = this.defaultVisibility ? 'block' : 'none'
    button.type = 'button'
    button.onclick = this.callback
    button.innerText = I18n.t('views.ke_map.reset_marker')

    return button
  }
}

type SearchResponse = {
  status: string
  display_status: string
  street: string
  city: string
  state: string
  country: string
  postal_code: string
  county: string
  formatted_address: string
  partial_match: string
  latitude: number
  longitude: number
}

type KeMapLayerGroups = {
  fallback: L.LayerGroup
  street: L.LayerGroup
  satellite: L.LayerGroup
  hybrid: L.LayerGroup
}

type MarkerInputs = {
  lat: HTMLInputElement
  lng: HTMLInputElement
}

type KeMapProps = {
  height: number
  defaultLat: number
  defaultLng: number
  defaultZoom: number
  mappableContainerId: string
  geocodedLat: number
  geocodedLng: number
  draggableMarker: boolean
  locationModel: ProgramLocationProps
  showLocationInfoControl: boolean
  markerMoveLatTargetSelector: string
  markerMoveLngTargetSelector: string
}

type KeMapState = {
  map: L.Map
  mapId: string
  loading: boolean
  resetMarkerEnabled: boolean
  errorMessage: string
  marker: L.Marker
  geocodedLat: number
  geocodedLng: number
  geocodedAddress: string
  mappableLat: number
  mappableLng: number
  draggableMarker: boolean
  mappableFields: object
  geocodeUpdateTimeout: number
  coordinateUpdateTimeout: number
  layers: KeMapLayerGroups
}

class KeMap extends Component<KeMapProps, KeMapState> {
  public static defaultProps = {
    height: 400,
    defaultLat: 39,
    defaultLng: -100,
    defaultZoom: 3,
    draggableMarker: false,
  }

  private loader: React.RefObject<HTMLDivElement>

  constructor(props: KeMapProps) {
    super(props)
    this.loader = React.createRef()

    this.state = {
      map: null,
      mapId: `leaflet-map-${Date.now()}`,
      errorMessage: '',
      loading: true,
      resetMarkerEnabled: false,
      marker: null,
      geocodedLat: null,
      geocodedLng: null,
      geocodedAddress: null,
      mappableLat: null,
      mappableLng: null,
      draggableMarker: props.draggableMarker || false,
      mappableFields: {},
      geocodeUpdateTimeout: null,
      coordinateUpdateTimeout: null,
      layers: {
        fallback: this.initFallbackLayers(),
        street: this.initStreetLayers(),
        satellite: this.initSatelliteLayers(),
        hybrid: this.initHybridLayers(),
      },
    }
  }

  get mappableAddressFromState(): string {
    return `${this.state.mappableFields['addr1']}, ${this.state.mappableFields['city']}, ${this.state.mappableFields['state_code']} ${this.state.mappableFields['postal_code']}`
  }

  get map(): L.Map {
    return this.state.map
  }

  get marker(): L.Marker {
    return this.state.marker
  }

  get isCoordinateLocation(): boolean {
    const checked = document.querySelector(
      `#${this.props.mappableContainerId} [data-location-type-field]:checked`
    ) as HTMLInputElement

    return checked != null && checked.value === 'coordinates'
  }

  // The hidden inputs that carry the street pin (mappable_*). A map mounted without them, like the
  // display pages' maps, has no street point to read or write.
  get markerInputs(): MarkerInputs | null {
    const { markerMoveLatTargetSelector, markerMoveLngTargetSelector } = this.props

    if (markerMoveLatTargetSelector == null || markerMoveLngTargetSelector == null) return null

    return {
      lat: document.querySelector(markerMoveLatTargetSelector) as HTMLInputElement,
      lng: document.querySelector(markerMoveLngTargetSelector) as HTMLInputElement,
    }
  }

  // Street mode's pin. Storefront always fills the marker inputs: the country-center seed, then
  // every lookup and every drag.
  get streetPoint(): L.LatLng | null {
    const inputs = this.markerInputs

    if (inputs == null) return null

    return toLatLng(inputs.lat.value, inputs.lng.value)
  }

  // The typed point. Null outside Coordinates mode and while the inputs don't read as numbers.
  get coordinatePoint(): L.LatLng | null {
    if (!this.isCoordinateLocation) return null

    return toLatLng(
      this.coordinateField('geocode_latitude').value,
      this.coordinateField('geocode_longitude').value
    )
  }

  render(): React.ReactNode {
    return (
      <React.Fragment>
        <p>{this.state.errorMessage}</p>
        <div className="ke-map static-map">
          <div id={this.state.mapId} style={{ height: this.props.height + 'px' }}>
            <div className="map-loader" ref={this.loader}>
              <div className="spinner-border text-light" role="status">
                <span className="sr-only">{I18n.t('views.ke_map.loading')}</span>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  componentDidMount(): void {
    const model = this.props.locationModel
    const modelPoint = model != null ? toLatLng(model.latitude, model.longitude) : null
    const coords = modelPoint || L.latLng(this.props.defaultLat, this.props.defaultLng)

    this.initializeMap(coords, this.props.defaultZoom)

    if (this.props.mappableContainerId != null) {
      this.populateExistingMappableFields()
      this.addMapableFieldListeners()
      this.addCoordinateFieldListeners()
      this.addLocationTypeListeners()
    }
  }

  initializeMap = (coords: L.LatLng, zoom: number): void => {
    const map = L.map(this.state.mapId, {
      // Options Here
      attributionControl: false, // Overide so we can set prefix: false
      zoomControl: false, // override to translate zoom titles
    }).setView(coords, zoom)

    L.control
      .zoom({
        zoomInTitle: I18n.t('views.ke_map.zoom_in'),
        zoomOutTitle: I18n.t('views.ke_map.zoom_out'),
      })
      .addTo(map)

    L.control
      .attribution({
        prefix: false,
      })
      .addTo(map)

    if (this.props.locationModel != null && this.props.showLocationInfoControl) {
      const locationTooltip = new LocationInfoControl(this.props.locationModel, {
        position: 'topright',
      } as L.ControlOptions)
      locationTooltip.addTo(map)
    }

    this.setupLayers(map)

    const marker = L.marker(coords, {
      draggable: this.state.draggableMarker,
    }).addTo(map)
    marker.addEventListener('moveend', this.handleMarkerMove)

    let enableResetMarkerButton = false
    if (
      this.props.geocodedLat != null &&
      this.props.geocodedLng != null &&
      !coords.equals(L.latLng(this.props.geocodedLat, this.props.geocodedLng))
    ) {
      enableResetMarkerButton = true
    }

    if (this.state.draggableMarker) {
      const resetMarkerButton = new ResetMarkerButton(
        this.resetMarker.bind(this),
        { position: 'bottomright' } as L.ControlOptions,
        enableResetMarkerButton
      )
      resetMarkerButton.addTo(map)
    }

    const stateMutation = {
      map: map,
      mapId: this.state.mapId,
      loading: this.state.loading,
      marker: marker,
      resetMarkerEnabled: enableResetMarkerButton,
      geocodedLat: this.state.geocodedLat,
      geocodedLng: this.state.geocodedLng,
    }
    if (this.props.geocodedLat != null && this.props.geocodedLng != null) {
      stateMutation.geocodedLat = this.props.geocodedLat
      stateMutation.geocodedLng = this.props.geocodedLng
    }
    if (stateMutation.geocodedLat == null && stateMutation.geocodedLng == null) {
      stateMutation.geocodedLat = coords.lat
      stateMutation.geocodedLng = coords.lng
    }

    this.setState(stateMutation, () => {
      this.hideLoader()
    })
  }

  disableMap = (): void => {
    this.map.dragging.disable()
    this.map.touchZoom.disable()
    this.map.doubleClickZoom.disable()
    this.map.scrollWheelZoom.disable()
    this.map.boxZoom.disable()
    this.map.keyboard.disable()
    if (this.map.tap) this.map.tap.disable()
    document.getElementById(this.state.mapId).style.cursor = 'default'
  }

  enableMap = (): void => {
    this.map.dragging.enable()
    this.map.touchZoom.enable()
    this.map.doubleClickZoom.enable()
    this.map.scrollWheelZoom.enable()
    this.map.boxZoom.enable()
    this.map.keyboard.enable()
    if (this.map.tap) this.map.tap.enable()
    document.getElementById(this.state.mapId).style.cursor = 'grab'
  }

  showLoader = (): void => {
    this.setState({ loading: true }, () => {
      this.loader.current.style.display = 'flex'
      this.disableMap()
    })
  }

  hideLoader = (): void => {
    this.setState({ loading: false }, () => {
      this.loader.current.style.display = 'none'
      this.enableMap()
    })
  }

  resetMarker = (): void => {
    this.marker.setLatLng(L.latLng(this.state.geocodedLat, this.state.geocodedLng))

    const newLat = String(this.state.geocodedLat)
    const newLong = String(this.state.geocodedLng)
    this.setFormFieldLatLong(newLat, newLong)

    this.setResetMarkerDisplay(false)
    this.setState({
      resetMarkerEnabled: false,
    })
  }

  populateExistingMappableFields = (): void => {
    const mappableFields = document.querySelectorAll(
      `#${this.props.mappableContainerId} [data-mappable-field]`
    )
    const stateMutation = {}
    mappableFields.forEach((field, _index) => {
      stateMutation[field.getAttribute('data-mappable-field')] = (field as HTMLInputElement).value
    })
    this.setState({ mappableFields: stateMutation }, this.alignMarkerOnLoad)
  }

  alignMarkerOnLoad = (): void => {
    if (this.isCoordinateLocation) {
      this.rememberStreetPoint()
      this.alignMarkerToCoordinateFields()
    } else {
      this.handleGeocodeUpdate()
    }
  }

  // A Coordinates-mode load starts the pin on the typed point, which initializeMap remembers as the
  // geocode. Remembering the street point instead keeps Reset on a street point.
  rememberStreetPoint = (): void => {
    const point = this.streetPoint

    if (point == null) return

    this.setState({ geocodedLat: point.lat, geocodedLng: point.lng })
  }

  addMapableFieldListeners = (): void => {
    const mappableFields = document.querySelectorAll(
      `#${this.props.mappableContainerId} [data-mappable-field]`
    )
    mappableFields.forEach((field, _index) => {
      field.addEventListener('keyup', (e) => {
        const eventTarget = e.target as HTMLInputElement
        this.setState(
          {
            mappableFields: {
              ...this.state.mappableFields,
              [eventTarget.getAttribute('data-mappable-field')]: eventTarget.value,
            },
          },
          () => {
            clearTimeout(this.state.geocodeUpdateTimeout)
            this.setState({
              geocodeUpdateTimeout: window.setTimeout(this.handleGeocodeUpdate, 600),
            })
          }
        )
      })
    })
  }

  addCoordinateFieldListeners = (): void => {
    const coordinateFields = document.querySelectorAll(
      `#${this.props.mappableContainerId} [data-coordinate-field]`
    )

    coordinateFields.forEach((field) => {
      field.addEventListener('keyup', () => {
        clearTimeout(this.state.coordinateUpdateTimeout)
        this.setState({
          coordinateUpdateTimeout: window.setTimeout(this.alignMarkerAfterKeyup, 600),
        })
      })
    })
  }

  addLocationTypeListeners = (): void => {
    const typeInputs = document.querySelectorAll(
      `#${this.props.mappableContainerId} [data-location-type-field]`
    )

    typeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (this.isCoordinateLocation) {
          // An address lookup's message says nothing about a typed point
          this.clearErrorMsg()
          this.setResetMarkerDisplay(false)
          this.alignMarkerToCoordinateFields()
        } else {
          this.alignMarkerToStreetPosition()
        }
      })
    })
  }

  // Coordinates mode's pin follows the Latitude/Longitude inputs; the remembered street geocode is
  // left alone.
  alignMarkerToCoordinateFields = (): void => {
    const point = this.coordinatePoint

    if (point == null) return

    this.map.setView(point, POINT_ZOOM)
    this.marker.setLatLng(point)
  }

  // The debounced keyup. A key that leaves the point on the pin (Tab, an arrow key, a copy) keeps
  // the instructor's view of the map, and one still pending after a switch to Street does nothing.
  alignMarkerAfterKeyup = (): void => {
    const point = this.coordinatePoint

    if (point != null && this.marker.getLatLng().equals(point)) return

    this.alignMarkerToCoordinateFields()
  }

  // Back to the street pin, with Reset if a drag is still pending. City, State and Postal Code stay
  // editable in Coordinates mode, so the lookup runs when the address changed since the last one;
  // the pin then follows the new address, as it does for an address edit in Street mode.
  alignMarkerToStreetPosition = (): void => {
    const point = this.streetPoint

    if (point != null) {
      this.map.setView(point, this.map.getZoom())
      this.marker.setLatLng(point)
    }

    this.setResetMarkerDisplay(this.state.resetMarkerEnabled)

    if (this.mappableAddressFromState !== this.state.geocodedAddress) this.handleGeocodeUpdate()
  }

  coordinateField = (name: string): HTMLInputElement => {
    return document.querySelector(
      `#${this.props.mappableContainerId} [data-coordinate-field="${name}"]`
    ) as HTMLInputElement
  }

  // Only a map with a draggable marker has the Reset button
  setResetMarkerDisplay = (visible: boolean): void => {
    if (!this.state.draggableMarker) return

    const resetMarkerButton = document.querySelector(
      `#${this.state.mapId} .reset-marker-button`
    ) as HTMLElement

    resetMarkerButton.style.display = visible ? 'block' : 'none'
  }

  handleMarkerMove = (e: L.DragEndEvent): void => {
    if (this.markerInputs != null) {
      const markerNewCoords = L.latLng(e.target._latlng.lat, e.target._latlng.lng)
      const newLat = markerNewCoords.lat.toString()
      const newLong = markerNewCoords.lng.toString()

      if (this.isCoordinateLocation) {
        // The pin is the typed point: a drag rewrites Latitude/Longitude, leaving no nudge to
        // reset. A drop on a repeated copy of the world is the same place, so a longitude past
        // -180 or 180 is brought back between them.
        const droppedOnWorldCopy = Math.abs(markerNewCoords.lng) > 180
        const longitude = droppedOnWorldCopy ? markerNewCoords.wrap().lng.toString() : newLong

        this.coordinateField('geocode_latitude').value = newLat
        this.coordinateField('geocode_longitude').value = longitude

        return
      }

      this.setFormFieldLatLong(newLat, newLong)

      if (!this.state.resetMarkerEnabled) {
        this.setResetMarkerDisplay(true)
        this.setState({
          resetMarkerEnabled: true,
        })
      }
    }
  }

  // Coordinates mode never looks the address up. A successful lookup remembers the address it
  // looked up, so a later switch back to Street can tell whether the address changed. A failure
  // that lands in Coordinates mode stays quiet, and the switch back to Street retries the lookup.
  handleGeocodeUpdate = (): void => {
    if (this.isCoordinateLocation || !this.hasSearchableDetails) return

    const address = this.mappableAddressFromState

    this.showLoader()
    fetch(`/places/search?q=${encodeURIComponent(address)}`)
      .then(this.checkStatus)
      .then((resp: Response) => resp.json())
      .then(this.throwNoResults)
      .then(this.setMapLatLong)
      .then(() => this.setState({ geocodedAddress: address }))
      .then(this.clearErrorMsg)
      .catch((e) => {
        if (!this.isCoordinateLocation) this.setState({ errorMessage: e.message })
      })
      .finally(this.hideLoader)
  }

  get hasSearchableDetails(): boolean {
    const fields = this.state.mappableFields

    return fields['addr1'] && (fields['postal_code'] || (fields['city'] && fields['state_code']))
  }

  checkStatus = (response: Response): Response => {
    if (response.status != 200) throw { message: I18n.t('views.ke_map.something_went_wrong') }

    return response
  }

  throwNoResults = (parsed: SearchResponse): SearchResponse => {
    if (parsed.status === 'No Results') {
      throw { message: parsed.display_status }
    }
    return parsed
  }

  setMapLatLong = (parsed: SearchResponse): void => {
    const { latitude, longitude } = parsed

    if (this.state.geocodedLat != latitude || this.state.geocodedLng != longitude) {
      this.setState(
        {
          mappableLat: latitude,
          mappableLng: longitude,
          geocodedLat: latitude,
          geocodedLng: longitude,
        },
        () => {
          // A lookup started in Street that lands after a switch to Coordinates refreshes the
          // street point but leaves the pin on the typed coordinates
          if (!this.isCoordinateLocation) {
            const coords = L.latLng(this.state.geocodedLat, this.state.geocodedLng)
            this.map.setView(coords, POINT_ZOOM)
            this.marker.setLatLng(coords)
          }

          const newLat = String(this.state.geocodedLat)
          const newLong = String(this.state.geocodedLng)
          this.setFormFieldLatLong(newLat, newLong)
        }
      )
    }
  }

  setFormFieldLatLong = (latitude: string, longitude: string): void => {
    const inputs = this.markerInputs

    if (inputs == null) return

    inputs.lat.value = latitude
    inputs.lng.value = longitude
  }

  clearErrorMsg = (): void => {
    this.setState({ errorMessage: '' })
  }

  setupLayers = (map: L.Map): void => {
    // In case one of the "main" layer groups goes offline, we fallback to these
    this.state.layers.fallback.addTo(map)
    // Default Layer Group
    this.state.layers.street.addTo(map)
    // Setup selectable Layer Groups
    L.control
      .layers({
        [I18n.t('views.ke_map.street_layer')]: this.state.layers.street,
        [I18n.t('views.ke_map.satellite_layer')]: this.state.layers.satellite,
        [I18n.t('views.ke_map.hybrid_layer')]: this.state.layers.hybrid,
      })
      .addTo(map)
  }

  initFallbackLayers = (): L.LayerGroup => {
    return L.layerGroup([
      // OpenStreetMap Latest
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }),
    ])
  }

  initStreetLayers = (): L.LayerGroup => {
    return L.layerGroup([
      // Stamen Terrain Base
      L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.png', {
        maxZoom: 17,
        attribution: I18n.t('views.ke_map.attribution_html'),
      }),
      // Stamen Watercolor
      L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/watercolor/{z}/{x}/{y}.jpg', {
        maxZoom: 17,
        minZoom: 1,
      }),
      // Stamen Terrain Overlay
      L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.png', {
        maxZoom: 17,
        opacity: 0.45,
        className: 'mix-multiple',
      }),
      // Stamen Terrain Labels
      L.tileLayer('https://stamen-tiles-{s}.a.ssl.fastly.net/terrain-labels/{z}/{x}/{y}.png', {
        maxZoom: 17,
      }),
    ])
  }

  initSatelliteLayers = (): L.LayerGroup => {
    return L.layerGroup([
      // Esri World Imagery
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          maxZoom: 19,
          attribution: `Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping,
        Aerogrid, IGN, IGP, UPR-EGP, GIS Editors`,
        }
      ),
      // Esri World Boundaries And Places
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_And_Places/MapServer/tile/{z}/{y}/{x}',
        {
          maxZoom: 19,
        }
      ),
    ])
  }

  initHybridLayers = (): L.LayerGroup => {
    return L.layerGroup([
      // Esri World Imagery
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          maxZoom: 19,
          attribution: `Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping,
        Aerogrid, IGN, IGP, UPR-EGP, GIS Editors`,
        }
      ),
      // Esri World Boundaries And Places
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_And_Places/MapServer/tile/{z}/{y}/{x}',
        {
          maxZoom: 19,
        }
      ),
      // Esri World Transportation
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}',
        {
          maxZoom: 19,
        }
      ),
    ])
  }
}

export default KeMap
